import {UserAppellation} from '@modules/husleie-framework/src/enums/UserAppellation.enum';
import {UserTitle} from '@modules/husleie-framework/src/enums/UserTitle.enum';
import AppClass from '@modules/husleie-framework/src/static-classes/App.class';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import {I18N} from '@modules/husleie-framework/src/types/I18N.entity';
import {fetchTranslationMessages} from '@modules/i18n/i18nLoadTranslations';
import i18nCommon from '@modules/i18n/mixins/Common';
import I18nOperations from '@modules/i18n/utils/I18nOperations.class';
import {StaticDataType} from '@modules/store/common/enums/StaticDataType.enum';
import Features from '@modules/store/common/types/Features.entity';
import FetchFeaturesConfig from '@modules/store/common/types/FetchFeaturesConfig.entity';
import FetchTranslationsConfig from '@modules/store/common/types/FetchTranslationsConfig.entity';
import FetchTranslationsConfigEntry from '@modules/store/common/types/FetchTranslationsConfigEntry.entity';
import FetchValidationRulesConfig from '@modules/store/common/types/FetchValidationRulesConfig.entity';
import ValidationRuleEntries from '@modules/store/common/types/ValidationRuleEntries.entity';
import * as moment from 'moment';
import Vue, {VueConstructor} from 'vue';
import {mapActions} from 'vuex';

export default (Vue as VueConstructor<Vue & InstanceType<typeof i18nCommon>>).extend({
  name: 'AppLoader',

  mixins: [i18nCommon],

  data() {
    return {
      internalDataLoaded: false,
    };
  },

  computed: {
    appDataLoaded(): boolean {
      return this.internalDataLoaded;
    },
  },

  methods: {
    ...mapActions('common/validation', ['fetchValidationRules']),
    ...mapActions('common/features', ['fetchFeatures']),
    ...mapActions('common/staticData', ['fetchTranslations']),

    getMetaInfo(title: string, locale: null | string, loadZendesk = false): any {
      const metaInfo: any = {
        htmlAttrs: {
          lang: I18nOperations.getLanguageCodeForLocale(locale),
          class: `${AppClass.getMarketIdentifier()?.toLowerCase()} h-full`,
        },
        title,
        meta: [
          { charset: 'utf-8' },
          {
            name: 'viewport',
            content: 'width=device-width,initial-scale=1.0',
          },
          { httpEquiv: 'X-UA-Compatible', content: 'IE=edge' },
        ],
        script: [],
        noscript: [{ innerHTML: 'This website requires JavaScript.' }],
      };

      if (loadZendesk) {
        switch (AppClass.getMarketIdentifier()) {
          case 'GER':
            metaInfo.script.push({
              id: 'ze-snippet',
              src: 'https://static.zdassets.com/ekr/snippet.js?key=9de933f8-a175-44eb-84f0-1d16a34fc649',
              onload: `zE('webWidget', 'setLocale', '${locale}')`,
            });
            metaInfo.script.push({
              innerHTML: `window.zESettings = {
                webWidget: {
                  position: { horizontal: 'right', vertical: 'bottom', mobile:{vertical:'top'} },
                  color: { theme: '#00c686', button: '#00c686', launcher: '#e9ecef', launcherText: '#00c686' },
                  offset: { vertical: '80px', mobile: { vertical: '80px'}}
                }
              };`,
              type: 'text/javascript',
              charset: 'utf-8',
            });
            break;
          case 'NOR':
            metaInfo.script.push({
              innerHTML: `/*<![CDATA[*/window.zEmbed||function(e,t){var
              n,o,d,i,s,a=[],r=document.createElement("iframe");window.zEmbed=function(){a.push(arguments)},window.zE=window.zE||window.zEmbed,r.src="javascript:false",r.title="",r.role="presentation",(r.frameElement||r).style.cssText="display: none",
              d=document.getElementsByTagName("script"),d=d[d.length-1],d.parentNode.insertBefore(r,d),i=r.contentWindow,s=i.document;try{o=s}catch(e){n=document.domain,
              r.src='javascript:var d=document.open();d.domain="'+n+'";void(0);',o=s}
              o.open()._l=function(){var e=this.createElement("script");n&&(this.domain=n),e.id="js-iframe-async",e.src="https://assets.zendesk.com/embeddable_framework/main.js",this.t=+new
              Date,this.zendeskHost="husleieno.zendesk.com",this.zEQueue=a,this.body.appendChild(e)},o.write('<body onload="document._l();">'),o.close()}();
              /*]]>*/`,
              type: 'text/javascript',
              charset: 'utf-8',
            });
            metaInfo.script.push({
              innerHTML: `window.zESettings = {
              webWidget: {
                position: { horizontal: 'right', vertical: 'top' },
                color: { theme: '#00c686', button: '#00c686', launcher: '#e9ecef', launcherText: '#00c686' },
                offset: { mobile: { vertical: '2px'}}
              }
            };`,
              type: 'text/javascript',
              charset: 'utf-8',
            });
            metaInfo.script.push({
              innerHTML: "zE('webWidget', 'setLocale', 'no');",
              type: 'text/javascript',
              charset: 'utf-8',
            });
            break;
        }
      }
      return metaInfo;
    },

    async loadApplication(
      name: string,
      translator: I18N,
      extendRule: FetchValidationRulesConfig['extendRule'],
      getValidationRules: (config: FetchValidationRulesConfig) => ValidationRuleEntries,
      getFeatures: () => Features,
      loadStaticData = true
    ): Promise<void> {
      this.setInternalDataLoaded(translator, false);

      const defaultLocale: string = AppClass.getDefaultLocale()!;
      const ietfLocale: string = I18nOperations.getIETFLocale(defaultLocale)!;

      moment.locale(I18nOperations.getLanguageCodeForLocale(ietfLocale));

      const messages = await fetchTranslationMessages(AppClass.getName(), defaultLocale, AppClass.getLocaleOrigin());
      this.$i18n.setLocaleMessage(defaultLocale, messages as any);
      this.$i18n.locale = defaultLocale;

      const promises = [];

      const fetchFeaturesConfig: FetchFeaturesConfig = {
        name,
        loader: getFeatures,
      };
      promises.push(this.fetchFeatures(fetchFeaturesConfig));

      const fetchValidationRulesConfig: FetchValidationRulesConfig = {
        name,
        dateFormat: 'DD.MM.YYYY', // todo, make it dynamic
        locale: translator.locale,
        loader: getValidationRules,
        translator,
        translatorProjectPrefix: this.translationsPrefix,
        extendRule,
      };
      promises.push(this.fetchValidationRules(fetchValidationRulesConfig));

      const translationEntries: FetchTranslationsConfigEntry[] = [];
      if (loadStaticData) {
        translationEntries.push({
          type: StaticDataType.TITLE,
          values: Object.values(UserTitle),
          translationPath: 'user.titles',
        });
        translationEntries.push({
          type: StaticDataType.APPELLATION,
          values: Object.values(UserAppellation),
          translationPath: 'user.appellations',
        });
      }
      const fetchTranslationsConfig: FetchTranslationsConfig = {
        locale: translator.locale,
        translator: translator,
        translatorProjectPrefix: this.translationsPrefix,
        entries: translationEntries,
      };
      promises.push(this.fetchTranslations(fetchTranslationsConfig));

      await Promise.all(promises)
        .then(() => {
          this.setInternalDataLoaded(translator, true);
        })
        .catch(error => LogOperations.error('AppLoader', error));
    },

    setInternalDataLoaded(translator: I18N, activator: boolean): void {
      this.internalDataLoaded = activator;
      translator.translationsLoaded = activator;
    },
  },
});
